import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ExternalLink, ShoppingBag, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import BusTour from "@/components/BusTour";
import CartDrawer from "@/components/CartDrawer";
import BookCard from "@/components/BookCard";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { amazonUrl, BOOKS, formatPrice } from "@/data/books";

export default function BookDetail() {
  const { slug } = useParams();
  const book = BOOKS.find((b) => b.slug === slug);
  const { add } = useCart();
  const [added, setAdded] = useState(false);

  if (!book) {
    return (
      <div className="grain min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-24">
          <div className="text-center">
            <p className="font-display font-bold text-2xl text-walnut mb-4">Book not found</p>
            <Link to="/books" className="text-terracotta font-semibold">Back to the collection</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const others = BOOKS.filter((b) => b.slug !== slug).slice(0, 3);

  const handleAdd = () => {
    add(book.slug);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/books"
            data-testid="back-to-books-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-walnutsoft hover:text-terracotta transition-colors mb-10"
          >
            <ArrowLeft size={16} /> All books
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-28">
            <motion.div
              data-testid="book-spotlight-frame"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="spotlight rounded-[2rem] border border-sand p-10 sm:p-14 lg:sticky lg:top-28 overflow-hidden"
            >
              <motion.img
                src={book.cover}
                alt={`${book.title} cover`}
                className="w-full max-w-sm mx-auto rounded-lg drop-shadow-[0_36px_44px_rgba(44,34,30,0.35)]"
                whileHover={{ scale: 1.04, rotate: -1.2 }}
                transition={{ type: "spring", stiffness: 180, damping: 16 }}
              />
            </motion.div>

            <div>
              <Eyebrow className="mb-4">Book {book.index} of 05 · {book.ages}</Eyebrow>
              <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl text-walnut">
                <MaskedLine i={0}>{book.title}</MaskedLine>
              </h1>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
              >
                {book.rating ? (
                  <p data-testid="book-rating" className="mt-4 flex items-center gap-2 text-sm text-walnutsoft">
                    <span className="flex text-amberbrand">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                      ))}
                    </span>
                    {book.rating.toFixed(1)} · {book.reviews} reviews on Amazon UK
                  </p>
                ) : (
                  <p data-testid="book-rating" className="mt-4 text-sm font-semibold text-sage">Brand new — be the first to review it</p>
                )}

                <p className="mt-6 text-base sm:text-lg text-walnutsoft leading-relaxed">{book.description}</p>
                <p className="mt-4 text-sm sm:text-base text-walnutsoft leading-relaxed">{book.longDescription}</p>

                <div className="mt-7 flex flex-wrap gap-2">
                  {book.themes.map((t) => (
                    <span key={t} className="rounded-full border border-sand bg-white/70 px-4 py-1.5 text-xs font-semibold text-walnutsoft">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-8 rounded-2xl border border-sand bg-white/70 p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amberbrand mb-4">What little readers take away</p>
                  <ul className="flex flex-col gap-2.5">
                    {book.outcomes.map((o) => (
                      <li key={o} className="flex items-start gap-3 text-sm text-walnutsoft">
                        <Check size={16} className="text-sage shrink-0 mt-0.5" /> {o}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-9 flex items-center gap-4 flex-wrap">
                  <span data-testid="book-price" className="font-display font-extrabold text-3xl sm:text-4xl text-walnut">
                    {formatPrice(book.price)}
                  </span>
                  <span className="text-xs text-walnutmuted">{book.format} · {book.year} · via Amazon UK</span>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    data-testid="buy-amazon-button"
                    href={amazonUrl(book)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex-1 rounded-full bg-terracotta text-cream py-4 px-6 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_18px_36px_-14px_rgba(224,109,83,0.6)]"
                  >
                    Buy on Amazon UK
                    <ExternalLink size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </a>
                  <button
                    data-testid="add-to-basket-button"
                    onClick={handleAdd}
                    className={`flex-1 rounded-full border-2 py-4 px-6 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 transition-colors duration-300 ${
                      added ? "border-sage bg-sage text-cream" : "border-walnut text-walnut hover:bg-walnut hover:text-cream"
                    }`}
                  >
                    {added ? <Check size={17} /> : <ShoppingBag size={17} />}
                    {added ? "Added to basket" : "Add to basket"}
                  </button>
                </div>
                <p className="mt-4 text-xs text-walnutmuted">Written by Damali Allison · Illustrated by Meni Etim</p>
              </motion.div>
            </div>
          </div>

          {book.slug === "bailey-in-london" && <BusTour />}

          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut">More from the series</h2>
              <Link to="/books" data-testid="more-books-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:gap-3 transition-all">
                View all <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {others.map((b, i) => (
              <BookCard key={b.slug} book={b} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
